import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Building, MessageSquare } from 'lucide-react';
import axios from 'axios';
import './Demo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Demo = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
    useCase: '',
    preferredDate: '',
    preferredTime: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/demo-requests`, formData);
      console.log('Demo request submitted:', response.data);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting demo request:', err);
      setError('Failed to submit demo request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="demo-page">
        <section className="success-section">
          <div className="success-content">
            <div className="success-icon">✓</div>
            <h1 className="display-large">Demo Request Received!</h1>
            <p className="body-large" style={{ marginTop: '24px' }}>
              Thank you for your interest in EAET. Our team will reach out within 24 hours to schedule your personalized demo.
            </p>
            <p className="body-medium" style={{ marginTop: '20px' }}>
              In the meantime, feel free to explore our documentation or check out case studies from similar companies.
            </p>
            <button 
              onClick={() => setSubmitted(false)} 
              className="btn-secondary"
              style={{ marginTop: '40px' }}
            >
              Submit Another Request
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <section className="page-hero">
        <h1 className="display-huge">Book a Demo</h1>
        <p className="body-large" style={{ marginTop: '32px', maxWidth: '800px', margin: '32px auto 0' }}>
          See EAET AI agents in action. Get a personalized demo tailored to your industry and use case.
        </p>
      </section>

      <section className="demo-form-section">
        <div className="demo-content">
          <div className="demo-info">
            <h2 className="display-medium">What to Expect</h2>
            <div className="info-item">
              <Clock size={24} color="var(--brand-primary)" />
              <div>
                <h3 className="heading-3">30-Minute Session</h3>
                <p className="body-small" style={{ marginTop: '8px' }}>Personalized walkthrough of EAET's capabilities for your industry.</p>
              </div>
            </div>
            <div className="info-item">
              <MessageSquare size={24} color="var(--brand-primary)" />
              <div>
                <h3 className="heading-3">Live Q&A</h3>
                <p className="body-small" style={{ marginTop: '8px' }}>Ask questions about features, pricing, and implementation.</p>
              </div>
            </div>
            <div className="info-item">
              <Building size={24} color="var(--brand-primary)" />
              <div>
                <h3 className="heading-3">Custom Solutions</h3>
                <p className="body-small" style={{ marginTop: '8px' }}>Learn how EAET can integrate with your existing systems.</p>
              </div>
            </div>
          </div>

          <div className="demo-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="body-small">
                  <User size={18} /> Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label className="body-small">
                  <Mail size={18} /> Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                />
              </div>

              <div className="form-group">
                <label className="body-small">
                  <Building size={18} /> Company Name *
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="Acme Corp"
                />
              </div>

              <div className="form-group">
                <label className="body-small">Industry *</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select your industry</option>
                  <option value="logistics">Logistics</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="autonomousvehicles">Autonomous Vehicles</option>
                  <option value="aibattery">AI Battery Management</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="body-small">Use Case Description *</label>
                <textarea
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="Tell us about your specific needs and challenges..."
                ></textarea>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="body-small">
                    <Calendar size={18} /> Preferred Date
                  </label>
                  <input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="body-small">
                    <Clock size={18} /> Preferred Time
                  </label>
                  <select
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleChange}
                  >
                    <option value="">Select time</option>
                    <option value="morning">Morning (9am - 12pm)</option>
                    <option value="afternoon">Afternoon (12pm - 5pm)</option>
                    <option value="evening">Evening (5pm - 7pm)</option>
                  </select>
                </div>
              </div>

              {error && (
                <div style={{ 
                  padding: '16px', 
                  background: 'rgba(255, 0, 0, 0.1)', 
                  border: '1px solid rgba(255, 0, 0, 0.3)',
                  marginTop: '20px',
                  color: '#ff6b6b'
                }}>
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="btn-primary" 
                style={{ width: '100%', marginTop: '32px' }}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Request Demo'}
              </button>

              <p className="body-small" style={{ marginTop: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                By submitting, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Demo;